import { useState, useEffect, useMemo } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useI18n } from '../i18n/I18nContext';
import { BFF_URL, consoleLinks } from '../config/domains';

const US_STATES = ['AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY','DC'];

export default function Apply() {
  const { t, locale } = useI18n();
  const [params] = useSearchParams();

  const initialPlan = params.get('plan') || 'starter';
  const initialFocus = params.get('focus') || '';
  const initialVolume = params.get('volume') || '';

  const [form, setForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    license_state: '',
    license_number: '',
    license_expiration: '',
    firm_name: '',
    years_experience: '',
    client_focus: initialFocus,
    monthly_volume: initialVolume,
    plan_tier: ['starter', 'professional', 'enterprise'].includes(initialPlan) ? initialPlan : 'starter',
    notes: ''
  });

  const [sending, setSending] = useState(false);
  const [submitted, setSubmitted] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (params.get('plan')) {
      const p = params.get('plan');
      if (['starter', 'professional', 'enterprise'].includes(p)) {
        setForm((f) => ({ ...f, plan_tier: p }));
      }
    }
  }, [params]);

  const stateOptions = useMemo(() => {
    return US_STATES.map((code) => ({ code, label: t(`states.${code}`) || code }));
  }, [t]);

  const focusOptions = useMemo(() => {
    return [
      { value: 'family', label: t('apply.clientFocusOptions.family') },
      { value: 'business', label: t('apply.clientFocusOptions.business') },
      { value: 'estate', label: t('apply.clientFocusOptions.estate') },
      { value: 'mixed', label: t('apply.clientFocusOptions.mixed') }
    ];
  }, [t]);

  const volumeOptions = useMemo(() => {
    return ['<5', '5-15', '15-50', '50+'].map((v) => ({ value: v, label: t(`apply.monthlyVolumeOptions.${v}`) }));
  }, [t]);

  const tierOptions = useMemo(() => {
    return [
      { value: 'starter', label: t('apply.planTierOptions.starter') },
      { value: 'professional', label: t('apply.planTierOptions.professional') },
      { value: 'enterprise', label: t('apply.planTierOptions.enterprise') }
    ];
  }, [t]);

  function update(field, value) {
    setForm((f) => ({ ...f, [field]: value }));
  }

  function validate() {
    const missing = [];
    if (!form.first_name.trim()) missing.push('first_name');
    if (!form.last_name.trim()) missing.push('last_name');
    if (!form.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) missing.push('email');
    if (!form.license_state) missing.push('license_state');
    if (!form.license_number.trim()) missing.push('license_number');
    if (!form.plan_tier) missing.push('plan_tier');
    return missing;
  }

  async function onSubmit(e) {
    e.preventDefault();
    setError('');
    const missing = validate();
    if (missing.length) {
      setError(t('apply.errors.required'));
      return;
    }
    setSending(true);
    try {
      const payload = {
        ...form,
        years_experience: form.years_experience ? parseInt(form.years_experience, 10) : null,
        license_expiration: form.license_expiration || null,
        notes: form.notes || null,
        locale
      };
      const res = await fetch(`${BFF_URL}/api/v1/advisor/apply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const body = await res.json().catch(() => ({}));
      if (res.status === 409) {
        setError(body.message || t('apply.errors.submit'));
        return;
      }
      if (!res.ok) {
        throw new Error(body.message || body.error || `HTTP ${res.status}`);
      }
      setSubmitted(body.application);
    } catch (err) {
      setError(err.message || t('apply.errors.submit'));
    } finally {
      setSending(false);
    }
  }

  return (
    <div className="mx-auto max-w-2xl px-4 py-12 md:py-16">
      <p className="landing-eyebrow">{t('apply.eyebrow')}</p>
      <h1 className="mt-3 text-3xl font-semibold tracking-tight md:text-4xl">{t('apply.title')}</h1>
      <p className="mt-3 text-sm text-slate-600">{t('apply.subtitle')}</p>
      <p className="mt-1 text-xs text-slate-400">{t('apply.requiredHint')}</p>

      {/* 已有账户 → 转跳 admin 登录 */}
      <div className="mt-6 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-3">
        <p className="text-sm text-slate-600">{t('apply.haveAccount')}</p>
        <a
          href={consoleLinks.login}
          rel="noopener noreferrer"
          className="inline-block rounded-full border border-coverfy-accent px-4 py-1.5 text-sm font-medium text-coverfy-accent active:scale-[0.98]"
        >
          {t('apply.loginCta')} →
        </a>
      </div>

      <AnimatePresence mode="wait">
        {submitted ? (
          <motion.div
            key="success"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-10 rounded-2xl border border-emerald-200 bg-emerald-50/50 p-6"
          >
            <p className="landing-eyebrow">{t('apply.successTitle')}</p>
            <p className="mt-2 text-sm text-coverfy-accentDark">{t('apply.successBody')}</p>
            <p className="mt-3 text-xs text-slate-500">
              Application ID: <code className="rounded bg-white px-1.5 py-0.5">{submitted.application_id}</code>
            </p>
            <div className="mt-4 flex gap-3">
              <a
                href={consoleLinks.login}
                rel="noopener noreferrer"
                className="inline-block rounded-full bg-coverfy-accent px-5 py-2.5 text-sm font-medium text-white active:scale-[0.98]"
              >
                {t('apply.goToConsole')}
              </a>
              <Link
                to="/"
                className="inline-block rounded-full border border-coverfy-accent px-5 py-2.5 text-sm font-medium text-coverfy-accent"
              >
                ←
              </Link>
            </div>
          </motion.div>
        ) : (
          <motion.form
            key="form"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            onSubmit={onSubmit}
            className="mt-8 space-y-4 rounded-2xl border border-slate-200 bg-white p-6 shadow-diffuse"
          >
            <div className="grid grid-cols-2 gap-3">
              <Field label={t('apply.firstName')} required>
                <input
                  type="text"
                  required
                  value={form.first_name}
                  onChange={(e) => update('first_name', e.target.value)}
                  className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                  autoComplete="given-name"
                />
              </Field>
              <Field label={t('apply.lastName')} required>
                <input
                  type="text"
                  required
                  value={form.last_name}
                  onChange={(e) => update('last_name', e.target.value)}
                  className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                  autoComplete="family-name"
                />
              </Field>
            </div>

            <Field label={t('apply.email')} required>
              <input
                type="email"
                required
                value={form.email}
                onChange={(e) => update('email', e.target.value)}
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                autoComplete="email"
              />
            </Field>

            <Field label={t('apply.phone')}>
              <input
                type="tel"
                value={form.phone}
                onChange={(e) => update('phone', e.target.value)}
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                placeholder="+1 604 555 0100"
                autoComplete="tel"
              />
            </Field>

            <div className="grid grid-cols-2 gap-3">
              <Field label={t('apply.licenseState')} required>
                <select
                  required
                  value={form.license_state}
                  onChange={(e) => update('license_state', e.target.value)}
                  className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm bg-white"
                >
                  <option value="">{t('common.selectOne')}</option>
                  {stateOptions.map((s) => (
                    <option key={s.code} value={s.code}>{s.code} · {s.label}</option>
                  ))}
                </select>
              </Field>
              <Field label={t('apply.licenseNumber')} required>
                <input
                  type="text"
                  required
                  value={form.license_number}
                  onChange={(e) => update('license_number', e.target.value)}
                  className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                />
              </Field>
            </div>

            <Field label={t('apply.licenseExpiration')}>
              <input
                type="date"
                value={form.license_expiration}
                onChange={(e) => update('license_expiration', e.target.value)}
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
              />
            </Field>

            <div className="grid grid-cols-2 gap-3">
              <Field label={t('apply.firmName')}>
                <input
                  type="text"
                  value={form.firm_name}
                  onChange={(e) => update('firm_name', e.target.value)}
                  className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                />
              </Field>
              <Field label={t('apply.yearsExperience')}>
                <input
                  type="number"
                  min="0"
                  max="80"
                  value={form.years_experience}
                  onChange={(e) => update('years_experience', e.target.value)}
                  className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                />
              </Field>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <Field label={t('apply.clientFocus')}>
                <select
                  value={form.client_focus}
                  onChange={(e) => update('client_focus', e.target.value)}
                  className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm bg-white"
                >
                  <option value="">{t('common.selectOne')}</option>
                  {focusOptions.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </Field>
              <Field label={t('apply.monthlyVolume')}>
                <select
                  value={form.monthly_volume}
                  onChange={(e) => update('monthly_volume', e.target.value)}
                  className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm bg-white"
                >
                  <option value="">{t('common.selectOne')}</option>
                  {volumeOptions.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </Field>
            </div>

            <Field label={t('apply.planTier')} required>
              <select
                required
                value={form.plan_tier}
                onChange={(e) => update('plan_tier', e.target.value)}
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm bg-white"
              >
                {tierOptions.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </Field>

            <Field label={t('apply.notes')}>
              <textarea
                rows={3}
                value={form.notes}
                onChange={(e) => update('notes', e.target.value)}
                placeholder={t('apply.notesPlaceholder')}
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
              />
            </Field>

            {error && <p className="text-sm text-red-600">{error}</p>}

            <button
              type="submit"
              disabled={sending}
              className="w-full rounded-full bg-coverfy-accent py-3 text-sm font-medium text-white active:scale-[0.98] disabled:opacity-50"
            >
              {sending ? t('apply.submitting') : t('apply.submit')}
            </button>
          </motion.form>
        )}
      </AnimatePresence>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block text-sm font-medium">
      {label}
      <div className="mt-1">{children}</div>
    </label>
  );
}
